import { get } from 'lodash';
import { Subject, debounceTime, takeUntil } from 'rxjs';

import { SelectionModel } from '@angular/cdk/collections';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CdkScrollable, ScrollDispatcher } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
  isSignal,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import {
  RowActionsConfig,
  SimpleTableConfig,
  TableActions,
  TableFilters,
} from '../../interfaces';
import { DialogService } from '../../services';

const keyToTitle = (key: string): string =>
  (key.split('.').pop() ?? key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

@Component({
  selector: 'phoenix-simple-table',
  templateUrl: './simple-table.component.html',
  styles: [
    `
      .mat-mdc-table-sticky {
        /* stylelint-disable-next-line declaration-no-important */
        z-index: 1 !important;
      }

      .simple-table {
        &__table {
          table-layout: fixed;
        }

        &__action-column::ng-deep {
          padding: 0;
          margin: 0;
          text-align: center;
        }

        &__detail-row {
          height: 0;
        }

        &__detail-wrapper {
          overflow: hidden;
          display: grid;
          grid-template-rows: 0fr;
          grid-template-columns: 100%;
          transition: grid-template-rows 225ms cubic-bezier(0.4, 0, 0.2, 1);
          width: 100%;

          &--expanded {
            grid-template-rows: 1fr;
          }
        }

        &__detail {
          display: flex;
          min-height: 0;
          width: 100%;
        }

        &__toggle-button {
          transition: transform 225ms cubic-bezier(0.4, 0, 0.2, 1);

          &--expanded {
            transform: rotate(180deg);
          }
        }

        &__row--hoverable {
          transition: background-color 120ms ease;

          &:hover {
            background-color: var(--theme-bg-4);
          }
        }
      }

      .simple-table-wrapper ::-webkit-scrollbar {
        -webkit-appearance: none;
        width: 7px;
        height: 7px;
      }

      .simple-table-wrapper ::-webkit-scrollbar-thumb {
        border-radius: 4px;
        background-color: gray;
        -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
      }
    `,
  ],
  standalone: false,
})
export class SimpleTableComponent
  implements OnChanges, AfterViewInit, OnInit, OnDestroy
{
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  scollContainer = viewChild<CdkScrollable>(CdkScrollable);
  columnFilterDialogForm = viewChild<TemplateRef<any>>(
    'columnFilterDialogForm'
  );

  @Input() dataSource!: MatTableDataSource<any>;
  @Input() tableConfig!: SimpleTableConfig;
  @Input() dataSourceLength = 0;
  @Input() pageIndex = 0;
  @Input() pageSize = 10;
  @Input() loadingTable = false;
  @Input() disableTableActions = false;
  @Input() searchBoxPlaceholder = 'Search...';
  @Input() highlightedRow!: any;
  @Input() initialSearchValue?: string;

  // Outputs
  @Output() pageChange = new EventEmitter();
  @Output() tableAction = new EventEmitter<TableActions>();
  @Output() sortTable = new EventEmitter();
  @Output() searchboxChange = new EventEmitter();
  @Output() selectionChange = new EventEmitter();
  @Output() scrollEnd = new EventEmitter();
  @Output() rowSelected = new EventEmitter();
  @Output() columnRemoved = new EventEmitter<string>();
  @Output() updatedColumns = new EventEmitter<string[]>();

  rowActionsConfig: RowActionsConfig | null = null;
  selection = new SelectionModel<unknown>(true, []);
  searchValue = new FormControl<string>('');
  expandedElement!: object | null;
  expandedElements: Set<object> = new Set();
  destroy$ = new Subject<void>();
  checkSignal = isSignal;
  lodashGet = get;

  scrollDispatcher = inject(ScrollDispatcher);
  dialogService = inject(DialogService);

  displayedColumns: string[] = [];
  displayedColumnsControl = new FormControl(
    [] as string[],
    Validators.required
  );

  ngOnInit(): void {
    if (this.initialSearchValue) {
      this.searchValue.setValue(this.initialSearchValue, { emitEvent: false });
    }

    if (this.tableConfig.hasInfiniteScroll) {
      this.scrollDispatcher
        .scrolled()
        .pipe(takeUntil(this.destroy$), debounceTime(200))
        .subscribe(result => {
          const target = result?.getElementRef().nativeElement;
          if (
            result === this.scollContainer() &&
            target &&
            target?.offsetHeight + target?.scrollTop >=
              target?.scrollHeight * 0.9 &&
            !this.loadingTable
          ) {
            this.scrollEnd.emit();
          }
        });
    }
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource?.data?.length ?? 0;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.clearSelection();
    } else {
      this.selection.select(...(this.dataSource?.data ?? []));
    }
    this.onSelectionChange();
  }

  onSelectionChange() {
    this.selectionChange.emit(this.rowsSelected);
  }

  clearSelection() {
    this.selection?.clear();
  }

  get rowsSelected() {
    return this.selection?.selected ?? [];
  }

  get tableExtraStyles() {
    const data = this.tableConfig.tableStyles ?? '';
    return isSignal(data) ? data : signal(data);
  }

  validateSelection() {
    if (this.tableConfig.serverSidePagination) {
      this.clearSelection();
      this.onSelectionChange();
    }
  }

  get tableFilters(): TableFilters {
    const { length, pageIndex, pageSize } = this.paginator || {};
    const { active = '', direction = '' } = this.sort || {};

    return {
      ...(this.paginator && {
        paginator: { length, pageIndex, pageSize },
      }),
      ...(this.tableConfig.isSortable && {
        sort: { active, direction },
      }),
      ...(this.tableConfig.hasSearchbox && {
        searchValue: this.searchValue.value || '',
      }),
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    this.buildTable();

    if (changes['dataSource']) {
      if (this.tableConfig.isSelectable && this.tableConfig.selectAllOnInit) {
        this.toggleAllRows();
      }
    }
  }

  ngAfterViewInit() {
    this.setPagination();
  }

  setPagination() {
    if (!this.tableConfig.serverSidePagination && this.dataSource) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    }
  }

  buildTable() {
    if (this.dataSource && this.tableConfig) {
      this.setPagination();
    }
    this.configureTableColumns();
  }

  configureTableColumns() {
    this.rowActionsConfig = this.tableConfig.rowActionsConfig ?? null;
    this.displayedColumns = this.filteredColumns;

    this.setFixedColumns();
  }

  setFixedColumns() {
    if (
      Array.isArray(this.tableConfig.rowActions) &&
      this.tableConfig.rowActions.length > 0
    ) {
      !this.displayedColumns.includes('row-actions') &&
        this.displayedColumns.push('row-actions');
    } else {
      this.displayedColumns = this.displayedColumns.filter(
        col => col !== 'row-actions'
      );
    }

    if (
      this.tableConfig.isSelectable &&
      !this.displayedColumns.includes('select')
    ) {
      this.displayedColumns.unshift('select');
    }

    if (
      this.tableConfig.isCollapsible &&
      !this.displayedColumns.includes('expand')
    ) {
      this.displayedColumns.unshift('expand');
    }
  }

  unfilteredIndex(val: unknown, index: number): number {
    let originalIndex = index;
    if (val && this.dataSource?.data) {
      const dataIndex = this.dataSource.data.indexOf(val);
      if (dataIndex !== -1) {
        originalIndex = dataIndex;
      }
    }
    return originalIndex;
  }

  onTableAction(val: unknown, type: string, index: number) {
    this.tableAction.emit({
      val,
      type,
      filters: this.tableFilters,
      index: this.unfilteredIndex(val, index),
    });
  }

  clickExpanded(element: any) {
    if (this.tableConfig.allowMultipleExpandedRows) {
      if (this.expandedElements.has(element)) {
        this.expandedElements.delete(element);
      } else {
        this.expandedElements.add(element);
      }
    } else {
      this.expandedElement = this.expandedElement === element ? null : element;
    }
  }

  isExpanded(element: any): boolean {
    return this.tableConfig.allowMultipleExpandedRows
      ? this.expandedElements.has(element)
      : this.expandedElement === element;
  }

  onSortTable() {
    this.validateSelection();
    this.sortTable.emit(this.tableFilters);
  }

  onSearchboxChange(data: string) {
    if (this.tableConfig.hasCustomSearch && this.tableConfig.filterPredicate) {
      this.dataSource.filterPredicate = this.tableConfig.filterPredicate;
    }

    this.validateSelection();
    this.searchValue.setValue(data);
    if (!this.tableConfig.serverSidePagination) {
      this.dataSource.filter = data.trim().toLowerCase();
    }
    this.searchboxChange.emit(this.tableFilters);
  }

  /** Used by the column filter, so a column without a title still has a name there. */
  getColumnName(key: string) {
    const title = this.tableConfig.columnDefList.find(
      col => col.key === key
    )?.title;
    return title || keyToTitle(key);
  }

  onPaginateChange() {
    this.validateSelection();
    this.pageChange.emit(this.tableFilters);
  }

  onRowClick(row: any) {
    if (this.tableConfig.highlightRowOnClick) {
      this.rowSelected.emit(row);
    }
  }

  get filteredColumns() {
    if (!this.tableConfig?.id || !this.tableConfig.columnFilter) {
      return [...(this.tableConfig?.displayedColumns || [])];
    }

    const stored = localStorage.getItem(
      `tableColumnFilter${this.tableConfig.id}`
    );
    if (!stored) {
      return [...this.tableConfig.displayedColumns];
    }

    try {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed.filter(col =>
          this.tableConfig.displayedColumns.includes(col)
        );
      }
    } catch (e) {
      console.warn('Error parsing stored columns:', e);
    }
    return [...this.tableConfig.displayedColumns];
  }

  onFilterButtonClick() {
    this.displayedColumnsControl.setValue(this.filteredColumns);

    const dialogRef = this.dialogService.form(
      this.columnFilterDialogForm() as TemplateRef<any>,
      this.displayedColumnsControl,
      {
        width: '500px',
        height: 'auto',
        data: {
          title: 'Column Filter',
          autoSaveOff: true,
        },
      }
    );

    const dialogComponent = dialogRef.componentInstance;
    dialogComponent.submitButton
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.displayedColumns = this.displayedColumnsControl.value as string[];
        localStorage.setItem(
          'tableColumnFilter' + this.tableConfig.id,
          JSON.stringify(this.displayedColumnsControl.value)
        );
        this.setFixedColumns();
        dialogRef.close();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onRemoveColumn(column: string) {
    this.columnRemoved.emit(column);
  }

  onMoveColumnLeft(column: string) {
    const index = this.displayedColumns.indexOf(column);
    [this.displayedColumns[index], this.displayedColumns[index - 1]] = [
      this.displayedColumns[index - 1],
      this.displayedColumns[index],
    ];
    this.onUpdateColumnOrder();
  }

  onMoveColumnRight(column: string) {
    const index = this.displayedColumns.indexOf(column);
    [this.displayedColumns[index], this.displayedColumns[index + 1]] = [
      this.displayedColumns[index + 1],
      this.displayedColumns[index],
    ];
    this.onUpdateColumnOrder();
  }

  canMoveLeft(columnKey: string): boolean {
    const index = this.displayedColumns.indexOf(columnKey);
    return index > 1;
  }

  canMoveRight(columnKey: string): boolean {
    const index = this.displayedColumns.indexOf(columnKey);
    return index < this.displayedColumns.length - 1;
  }

  onUpdateColumnOrder(): void {
    this.updatedColumns.emit(this.displayedColumns.slice(1));
  }

  dropListDropped(event: CdkDragDrop<any>): void {
    if (event) {
      moveItemInArray(
        this.displayedColumns,
        event.previousIndex + 1,
        event.currentIndex + 1
      );
      this.onUpdateColumnOrder();
    }
  }

  canRemoveColumn(): boolean {
    if (this.displayedColumns.length === 2) {
      return false;
    }
    return true;
  }

  isHighlighted(row: any): boolean {
    const sel = this.highlightedRow;
    if (!sel) return false;

    if (typeof sel === 'object' && sel !== null) {
      if ('id' in sel) {
        return row?.id === sel.id;
      } else if (
        this.tableConfig.idPath &&
        this.lodashGet(row, this.tableConfig.idPath) &&
        this.lodashGet(sel, this.tableConfig.idPath)
      ) {
        return (
          this.lodashGet(row, this.tableConfig.idPath) ===
          this.lodashGet(sel, this.tableConfig.idPath)
        );
      }
    } else if (typeof sel === 'string' && this.tableConfig.idPath) {
      return this.lodashGet(row, this.tableConfig.idPath) === sel;
    }

    return row?.id === sel;
  }

  isRowCorrupted(row: any): boolean {
    if (!this.tableConfig?.handleCorruptedData) {
      return false;
    }

    if (this.tableConfig.corruptedPath) {
      return this.lodashGet(row, this.tableConfig.corruptedPath) === true;
    }

    return row?.corrupted === true;
  }

  isPointerCursorEnabled(row: any): boolean {
    return (
      this.tableConfig.cellPointerOnRowClick === true &&
      this.tableConfig.highlightRowOnClick === true &&
      !this.loadingTable &&
      !this.isRowCorrupted(row)
    );
  }
}
